import classNames from 'classnames';
import type { Dispatch } from 'react';
import type { GroupBase, SingleValue } from 'react-select';

import { Select as RevelioSelect, Text } from '@revelio/ui';

import type { PrimaryFilterId } from '../../filters/types/filters';
import { isSubfilterableDimension } from '../Chart/diverging-bar/dynamic-diverging-combo';
import type { TabContentAction } from '../PageConfigProvider/page-reducer';
import {
  isCompositionSecondaryDimension,
  type SecondaryDimensionOption,
} from '../PageConfigProvider/types/dimension';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import labelStyles from './section-label.module.css';
import styles from './subfilter-dimension-selector.module.css';
import { getAvailableSegments } from './utils/segment-mapping';

type SubfilterDimensionSelectorProps = {
  item: PositionedItem;
  primaryFilterId: PrimaryFilterId;
  dispatch: Dispatch<TabContentAction>;
};

export const SubfilterDimensionSelector = ({
  item,
  primaryFilterId,
  dispatch,
}: SubfilterDimensionSelectorProps) => {
  const id = item?.id;
  const combo = item?.plot?.divergingBarCombo;

  const options: SecondaryDimensionOption[] = getAvailableSegments({
    chartType: item?.plot?.chartType,
    primaryFilterId,
    metrics: item?.plot?.defaultMetricOptions,
  }).filter(
    (option) =>
      option.value != null &&
      isCompositionSecondaryDimension(option.value) &&
      isSubfilterableDimension(option.value)
  );

  const selected =
    options.find((option) => option.value === combo?.subfilterDimension) ??
    null;

  const onChange = (option: SingleValue<SecondaryDimensionOption>) => {
    const nextDimension =
      option?.value != null && isCompositionSecondaryDimension(option.value)
        ? option.value
        : undefined;
    dispatch({
      type: 'UPDATE_ITEM_DIVERGING_BAR_COMBO',
      payload: {
        id,
        divergingBarCombo: { ...combo, subfilterDimension: nextDimension },
      },
    });
  };

  if (options.length === 0) return null;

  return (
    <div className={styles.section}>
      <Text
        className={classNames(labelStyles.sectionLabel, styles.label)}
        fontSize="sm"
      >
        Subfilter dimension
      </Text>
      <RevelioSelect<
        SecondaryDimensionOption,
        false,
        GroupBase<SecondaryDimensionOption>
      >
        options={options}
        value={selected}
        onChange={onChange}
        placeholder="None"
        isClearable
        isSearchable
        size="sm"
      />
    </div>
  );
};
